// src/pages/Dashboard.js (Aprimorado com visualizações dinâmicas e contextualização)
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { FinanceContext } from '../store/financeContext';
import { MarketingContext } from '../store/marketingContext';
import { TeamContext } from '../store/teamContext';
import { ProductContext } from '../store/productContext';
import { AuthContext } from '../store/authContext';

import {
  KpiCardGroup,
  MetricChart,
  PerformanceBar,
  InsightGenerator,
  DistributionPie,
  ScatterAnalysis
} from '../components/Visualization';

import useVisualizationData from '../hooks/useVisualizationData';
import { formatNumber, calculateDiffs } from '../utils/dataUtils';

import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  BarChart2,
  PieChart,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Activity,
  AlertTriangle,
  HelpCircle,
  Info,
  Users,
  ChevronRight,
  ChevronDown,
  Maximize2
} from 'lucide-react';

import GoalAnimation from '../components/UI/GoalAnimation';

const Dashboard = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [showInsights, setShowInsights] = useState(true);
  const [showAnimation, setShowAnimation] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);
  
  // Contextos
  const { cashFlow, monthlyGoal, goalAchieved, previousCashFlow = {} } = useContext(FinanceContext);
  const { totalInvestment, totalRevenue, totalROI, cpa, previousInvestment = 0, previousRevenue = 0, previousROI = 0, previousCPA = 0 } = useContext(MarketingContext);
  const { totalSalaries, previousSalaries = 0 } = useContext(TeamContext);
  const { products, getOverallProductMetrics } = useContext(ProductContext);
  const { currentUser } = useContext(AuthContext);

  useEffect(() => {
    if (goalAchieved) {
      setShowAnimation(true);
      const timer = setTimeout(() => setShowAnimation(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [goalAchieved]);

  // Dados principais para visualizações
  const { 
    data: financeData, 
    loading: financeLoading, 
    insights: financeInsights, 
    refresh: refreshFinance,
    anomalies: financeAnomalies 
  } = useVisualizationData({
    dataType: 'finance', 
    timeRange,
    metrics: [
      { key: 'income', label: 'Receita', aggregation: 'sum', higherIsBetter: true },
      { key: 'expenses', label: 'Despesas', aggregation: 'sum', higherIsBetter: false },
      { key: 'profit', label: 'Lucro', aggregation: 'sum', higherIsBetter: true },
      { key: 'roi', label: 'ROI', aggregation: 'avg', higherIsBetter: true, detectAnomalies: true }
    ],
    groupBy: 'month', 
    generateInsights: true, 
    detectAnomalies: true
  });

  // Dados de canais de marketing
  const { 
    data: channelData, 
    summary: channelSummary,
    previousData: previousChannelData
  } = useVisualizationData({
    dataType: 'marketing', 
    timeRange,
    metrics: [
      { key: 'investment', label: 'Investimento', aggregation: 'sum' },
      { key: 'revenue', label: 'Receita', aggregation: 'sum' },
      { key: 'roi', label: 'ROI', aggregation: 'avg' },
      { key: 'cpa', label: 'CPA', aggregation: 'avg', higherIsBetter: false }
    ],
    groupBy: 'channel',
    getPreviousData: true
  });

  // Dados de produtos
  const { 
    data: productData,
    previousData: previousProductData
  } = useVisualizationData({
    dataType: 'products', 
    timeRange,
    metrics: [
      { key: 'sales', label: 'Vendas', aggregation: 'sum' },
      { key: 'revenue', label: 'Receita', aggregation: 'sum' },
      { key: 'cost', label: 'Custo', aggregation: 'sum' },
      { key: 'profit', label: 'Lucro', aggregation: 'sum' }
    ],
    groupBy: 'product',
    getPreviousData: true
  });

  // Cálculos de métricas baseados em dados atuais e anteriores
  const totalExpenses = cashFlow.expenses + totalSalaries;
  const previousTotalExpenses = (previousCashFlow.expenses || 0) + previousSalaries;
  
  const netProfit = cashFlow.income - totalExpenses;
  const previousNetProfit = (previousCashFlow.income || 0) - previousTotalExpenses;
  
  const goalProgress = (netProfit / monthlyGoal) * 100;
  
  // Cálculos de variação percentual baseados em dados reais
  const incomeChange = previousCashFlow.income ? ((cashFlow.income - previousCashFlow.income) / previousCashFlow.income) * 100 : 0;
  const expensesChange = previousTotalExpenses ? ((totalExpenses - previousTotalExpenses) / previousTotalExpenses) * 100 : 0;
  const profitChange = previousNetProfit ? ((netProfit - previousNetProfit) / previousNetProfit) * 100 : 0;
  const goalProgressChange = previousNetProfit && monthlyGoal ? 
    (((netProfit / monthlyGoal) - (previousNetProfit / monthlyGoal)) / (previousNetProfit / monthlyGoal)) * 100 : 0;

  // Configuração dos KPIs principais com valores dinâmicos
  const kpiMetrics = [
    { 
      title: 'Faturamento', 
      value: cashFlow.income, 
      prefix: 'R$ ', 
      change: parseFloat(incomeChange.toFixed(1)), 
      timeframe: 'vs. período anterior', 
      color: 'blue', 
      trendData: financeData.slice(-6), 
      trendKey: 'income', 
      icon: <DollarSign className="h-5 w-5 text-blue-600" /> 
    },
    { 
      title: 'Despesas', 
      value: totalExpenses, 
      prefix: 'R$ ', 
      change: parseFloat(expensesChange.toFixed(1)), 
      timeframe: 'vs. período anterior', 
      color: 'red', 
      trendData: financeData.slice(-6), 
      trendKey: 'expenses', 
      inverseTrend: true, 
      icon: <TrendingDown className="h-5 w-5 text-red-600" /> 
    },
    { 
      title: 'Lucro', 
      value: netProfit, 
      prefix: 'R$ ', 
      change: parseFloat(profitChange.toFixed(1)), 
      timeframe: 'vs. período anterior', 
      color: 'green', 
      trendData: financeData.slice(-6), 
      trendKey: 'profit', 
      icon: <TrendingUp className="h-5 w-5 text-green-600" /> 
    },
    { 
      title: 'Meta Mensal', 
      value: goalProgress, 
      suffix: '%', 
      change: parseFloat(goalProgressChange.toFixed(1)), 
      timeframe: goalProgress > 100 ? 'Meta atingida!' : `Faltam R$ ${formatNumber(monthlyGoal - netProfit > 0 ? monthlyGoal - netProfit : 0)}`, 
      color: 'yellow', 
      trendData: financeData.slice(-6), 
      trendKey: 'goalProgress', 
      icon: <Target className="h-5 w-5 text-yellow-600" /> 
    }
  ];

  // Mapeamento de cores para canais
  const channelColors = {
    facebook: '#1877F2', 
    google: '#EA4335', 
    tiktok: '#000000', 
    taboola: '#1C68D4',
    instagram: '#C13584',
    youtube: '#FF0000',
    linkedin: '#0A66C2'
  };

  // Dados de distribuição de investimento por canal com valores percentuais
  const totalChannelInvestment = channelData.reduce((sum, channel) => sum + (channel.investment || 0), 0);
  
  const channelDistributionData = channelData.map(c => ({ 
    name: c.channel, 
    value: c.investment || 0,
    percent: totalChannelInvestment ? ((c.investment || 0) / totalChannelInvestment) * 100 : 0
  }));

  // Ordenar canais por investimento para o gráfico de pizza
  const sortedChannelDistributionData = [...channelDistributionData].sort((a, b) => b.value - a.value);

  // Configuração avançada do gráfico de fluxo de caixa com linhas de tendência e metas
  const cashFlowChartConfig = {
    data: financeData,
    series: [
      { 
        type: 'bar', 
        dataKey: 'income', 
        name: 'Receita', 
        color: '#3B82F6', 
        valueFormatter: val => `R$ ${val.toLocaleString()}` 
      },
      { 
        type: 'bar', 
        dataKey: 'expenses', 
        name: 'Despesas', 
        color: '#EF4444', 
        valueFormatter: val => `R$ ${val.toLocaleString()}` 
      },
      { 
        type: 'line', 
        dataKey: 'profit', 
        name: 'Lucro', 
        color: '#10B981', 
        valueFormatter: val => `R$ ${val.toLocaleString()}` 
      }
    ],
    xAxisDataKey: 'month',
    title: 'Fluxo de Caixa',
    subtitle: 'Análise mensal de receitas, despesas e lucro',
    height: 400,
    targets: [
      { 
        value: monthlyGoal, 
        label: `Meta (R$ ${formatNumber(monthlyGoal)})`, 
        color: '#F59E0B' 
      }
    ],
    showControls: true,
    annotations: financeAnomalies.map(anomaly => ({
      x: anomaly.month,
      y: anomaly[anomaly.metricKey],
      label: `Anomalia: ${anomaly.metricLabel}`,
      color: '#EF4444'
    }))
  };

  // Adicionar dados de último período para comparação nos canais de marketing
  const channelsWithComparison = useMemo(() => {
    if (!channelData || !previousChannelData) return channelData;
    
    // Criar mapa de dados anteriores para facilitar acesso
    const previousMap = {};
    previousChannelData.forEach(item => {
      if (item.channel) {
        previousMap[item.channel] = item;
      }
    });
    
    // Enriquecer dados atuais com comparações
    return channelData.map(channel => {
      const previous = previousMap[channel.channel];
      
      // Calcular variações percentuais
      const roiChange = previous && previous.roi ? 
        ((channel.roi || 0) - (previous.roi || 0)) / Math.abs(previous.roi || 1) * 100 : 0;
        
      const revenueChange = previous && previous.revenue ? 
        ((channel.revenue || 0) - (previous.revenue || 0)) / (previous.revenue || 1) * 100 : 0;
      
      // Retornar canal com dados adicionais
      return {
        ...channel,
        roiChange,
        revenueChange,
        previousRoi: previous?.roi || 0,
        previousRevenue: previous?.revenue || 0,
        previousInvestment: previous?.investment || 0
      };
    });
  }, [channelData, previousChannelData]);

  // Configuração aprimorada do gráfico de performance por canal
  const channelPerformanceConfig = {
    data: channelsWithComparison,
    series: [
      { 
        type: 'bar', 
        dataKey: 'investment', 
        name: 'Investimento', 
        color: '#A855F7', 
        valueFormatter: val => `R$ ${val.toLocaleString()}` 
      },
      { 
        type: 'bar', 
        dataKey: 'revenue', 
        name: 'Receita', 
        color: '#3B82F6', 
        valueFormatter: val => `R$ ${val.toLocaleString()}` 
      },
      { 
        type: 'line', 
        dataKey: 'roi', 
        name: 'ROI', 
        color: '#10B981', 
        valueFormatter: val => `${val.toFixed(1)}%`, 
        yAxisId: 'right',
        strokeWidth: 3
      }
    ],
    xAxisDataKey: 'channel',
    title: 'Performance por Canal',
    subtitle: 'Comparativo de investimento, receita e ROI',
    height: 350,
    showControls: true,
    tooltipFormatter: (value, name, props) => {
      if (name === 'roi') {
        const item = props.payload;
        return [
          `${value.toFixed(1)}% (${item.roiChange >= 0 ? '+' : ''}${item.roiChange.toFixed(1)}%)`,
          'ROI'
        ];
      }
      return [value, name];
    }
  };

  // Preparar dados avançados de produtos
  const productsWithMetrics = useMemo(() => {
    // Obter métricas gerais
    const overallMetrics = getOverallProductMetrics() || {};
    
    // Enriquecer dados de produtos
    return productData.map(product => {
      // Calcular métricas avançadas
      const roi = product.cost > 0 ? ((product.revenue - product.cost) / product.cost) * 100 : 0;
      const profitMargin = product.revenue > 0 ? (product.profit / product.revenue) * 100 : 0;
      
      return {
        ...product,
        roi,
        profitMargin,
        contribution: overallMetrics.totalRevenue ? 
          (product.revenue / overallMetrics.totalRevenue) * 100 : 0
      };
    });
  }, [productData, getOverallProductMetrics]);

  // Ordenar produtos por receita para o gráfico
  const topProducts = [...productsWithMetrics]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  // Configuração avançada para o gráfico de produtos
  const productBarConfig = {
    data: topProducts,
    dataKey: "revenue",
    nameKey: "name",
    orientation: "horizontal",
    showValues: true,
    valueFormatter: (val) => `R$ ${val.toLocaleString()}`,
    maxItems: 5,
    positiveColor: "#3B82F6",
    height: 350,
    sortBy: "value",
    showTarget: false
  };

  // Configuração avançada para o gráfico de scatter de ROI vs CPA
  const scatterConfig = {
    data: channelsWithComparison,
    xAxisDataKey: "cpa",
    yAxisDataKey: "roi",
    sizeKey: "investment",
    nameKey: "channel",
    xAxisLabel: "CPA (R$)",
    yAxisLabel: "ROI (%)",
    xAxisFormatter: (v) => `R$ ${v.toFixed(2)}`,
    yAxisFormatter: (v) => `${v.toFixed(1)}%`,
    quadrantLabels: [
      'Alto ROI / Baixo CPA (Ideal)', 
      'Alto ROI / Alto CPA',
      'Baixo ROI / Alto CPA (Problema)',
      'Baixo ROI / Baixo CPA'
    ],
    showQuadrants: true,
    annotations: channelsWithComparison
      .filter(channel => channel.roiChange && Math.abs(channel.roiChange) > 20)
      .map(channel => ({
        x: channel.cpa,
        y: channel.roi,
        label: `${channel.channel}: ${channel.roiChange > 0 ? '+' : ''}${channel.roiChange.toFixed(1)}% ROI`,
        color: channel.roiChange > 0 ? '#10B981' : '#EF4444'
      })),
    colors: channelsWithComparison.map(channel => channelColors[channel.channel] || '#3B82F6')
  };

  // Função para alternar a exibição do gráfico expandido
  const toggleExpandChart = (chartId) => {
    setExpandedChart(expandedChart === chartId ? null : chartId);
  };

  // Dados calculados para visualizações
  const totalRevenueProduct = productData.reduce((sum, product) => sum + product.revenue, 0);
  const hasAnomalies = financeAnomalies && financeAnomalies.length > 0;

  // Períodos em formato legível para exibição
  const periodDisplay = {
    '7d': 'nos últimos 7 dias',
    '30d': 'no último mês',
    '90d': 'no último trimestre',
    'year': 'no ano atual',
    'all': 'em todo o período'
  };

  return (
    <div className="page-container space-y-6">
      {showAnimation && <GoalAnimation />}

      {/* Modal para gráfico expandido */}
      {expandedChart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-bold text-xl">
                {expandedChart === 'cashflow' && 'Fluxo de Caixa Detalhado'}
                {expandedChart === 'channels' && 'Performance por Canal Detalhada'}
                {expandedChart === 'products' && 'Análise Detalhada de Produtos'}
                {expandedChart === 'scatter' && 'Análise ROI vs. CPA Detalhada'}
              </h3>
              <button 
                onClick={() => setExpandedChart(null)}
                className="p-1 rounded-full hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {expandedChart === 'cashflow' && (
                <div className="h-[600px]">
                  <MetricChart {...cashFlowChartConfig} height={550} />
                </div>
              )}
              {expandedChart === 'channels' && (
                <div className="h-[600px]">
                  <MetricChart {...channelPerformanceConfig} height={550} />
                </div>
              )}
              {expandedChart === 'products' && (
                <div className="h-[600px]">
                  <PerformanceBar {...productBarConfig} height={550} />
                </div>
              )}
              {expandedChart === 'scatter' && (
                <div className="h-[600px]">
                  <ScatterAnalysis {...scatterConfig} height={550} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cabeçalho com controles */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Dashboard Financeiro</h1>
          <p className="text-gray-600">
            Olá, <span className="font-semibold">{currentUser?.name}</span>!
            {goalProgress >= 100 && (
              <span className="ml-2 text-green-600 font-medium">
                Parabéns! Você atingiu {goalProgress.toFixed(0)}% da meta.
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-3 py-2 border-r border-gray-200">
              <Calendar size={18} className="text-gray-500" />
            </div>
            <select 
              className="py-2 px-3 text-sm text-gray-700 border-none focus:ring-0" 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
              <option value="90d">Últimos 90 dias</option>
              <option value="year">Este ano</option>
              <option value="all">Todo o período</option>
            </select>
          </div>
          
          <button 
            className="p-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 shadow-sm text-gray-700" 
            onClick={refreshFinance}
            title="Atualizar dados"
          >
            <RefreshCw size={18} />
          </button>
          
          <button 
            className="p-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 shadow-sm text-gray-700" 
            title="Filtros avançados"
          >
            <Filter size={18} />
          </button>
          
          <button 
            className="p-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 shadow-sm text-gray-700" 
            title="Exportar dados"
          >
            <Download size={18} />
          </button>
          
          <button 
            className="p-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 shadow-sm text-gray-700" 
            onClick={() => setShowHelp(!showHelp)}
            title="Ajuda"
          >
            <HelpCircle size={18} />
          </button>
        </div>
      </div>

      {/* Mensagens de alerta/informação contextual */}
      {showHelp && (
        <div className="alert alert-info">
          <div className="alert-icon">
            <Info className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-medium">Como usar este dashboard</h3>
            <p className="text-sm">Este dashboard apresenta uma visão completa do desempenho financeiro e de marketing do seu negócio. Use o seletor de período no topo para visualizar dados de diferentes intervalos de tempo. Clique no ícone de expandir em cada gráfico para uma visualização detalhada.</p>
          </div>
        </div>
      )}
      
      {hasAnomalies && (
        <div className="alert alert-warning">
          <div className="alert-icon">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-medium">Anomalias detectadas</h3>
            <p className="text-sm">Foram detectadas {financeAnomalies.length} anomalias nos dados financeiros {periodDisplay[timeRange]}. Verifique os pontos destacados no gráfico de Fluxo de Caixa.</p>
          </div>
        </div>
      )}

      {/* Cards de KPI principais com dados calculados dinamicamente */}
      <KpiCardGroup metrics={kpiMetrics} layout="grid-medium" />

      {/* Insights automáticos baseados nos dados */}
      {showInsights && financeInsights.length > 0 && (
        <div className="card shadow-md">
          <div className="card-header">
            <h2 className="card-title flex items-center">
              <Activity className="h-5 w-5 text-blue-600 mr-2" />
              Insights Automáticos
            </h2>
            <button 
              className="text-sm text-gray-500 hover:text-gray-700" 
              onClick={() => setShowInsights(false)}
            >
              Ocultar
            </button>
          </div>
          <div className="p-6">
            <InsightGenerator
              data={financeData}
              previousData={[]}
              metrics={[
                { key: 'income', label: 'Receita', higherIsBetter: true },
                { key: 'expenses', label: 'Despesas', higherIsBetter: false },
                { key: 'profit', label: 'Lucro', higherIsBetter: true }
              ]}
              targets={{ 
                income: monthlyGoal * 1.5, 
                profit: monthlyGoal 
              }}
              significanceThreshold={5}
              period={periodDisplay[timeRange] || `no período selecionado`}
            />
          </div>
        </div>
      )}

      {/* Gráficos principais com controles de expansão */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="card shadow-md overflow-hidden">
            <div className="card-header">
              <h2 className="card-title flex items-center">
                <BarChart2 className="h-5 w-5 text-blue-600 mr-2" />
                Fluxo de Caixa
              </h2>
              <button 
                className="p-1 rounded-lg hover:bg-gray-100 text-gray-600" 
                onClick={() => toggleExpandChart('cashflow')}
                title="Expandir gráfico"
              >
                <Maximize2 size={18} />
              </button>
            </div>
            <div className="p-6">
              <MetricChart {...cashFlowChartConfig} />
            </div>
            {hasAnomalies && (
              <div className="px-6 pb-6">
                <p className="text-sm text-amber-600 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Detecção de anomalias ativada. Pontos vermelhos indicam valores fora do padrão.
                </p>
              </div>
            )}
          </div>
        </div>
        
        <div className="card shadow-md h-full">
          <div className="card-header">
            <h2 className="card-title flex items-center">
              <PieChart className="h-5 w-5 text-blue-600 mr-2" />
              Distribuição por Canal
            </h2>
          </div>
          <div className="p-6 h-96">
            <DistributionPie
              data={sortedChannelDistributionData}
              nameKey="name"
              dataKey="value"
              donut
              colors={Object.values(channelColors)}
              centerLabel="Total"
              centerValue={`R$ ${formatNumber(channelSummary.investment?.value || totalChannelInvestment)}`}
              valueFormatter={(val) => `R$ ${val.toLocaleString()}`}
              tooltipFormatter={(value, name, entry) => {
                return `${entry.name}: R$ ${value.toLocaleString()} (${entry.percent.toFixed(1)}%)`;
              }}
            />
          </div>
          <div className="px-6 pb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Distribuição percentual</h3>
            <div className="space-y-2">
              {sortedChannelDistributionData.slice(0, 3).map((channel, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: channelColors[channel.name] || '#3B82F6' }}
                    ></div>
                    <span className="text-sm">{channel.name}</span>
                  </div>
                  <span className="text-sm font-medium">{channel.percent.toFixed(1)}%</span>
                </div>
              ))}
              {sortedChannelDistributionData.length > 3 && (
                <button className="text-xs text-blue-600 flex items-center">
                  Ver todos <ChevronRight className="h-3 w-3 ml-1" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico de performance por canal com controles de expansão */}
      <div className="card shadow-md">
        <div className="card-header">
          <h2 className="card-title flex items-center">
            <BarChart2 className="h-5 w-5 text-blue-600 mr-2" />
            Performance por Canal
          </h2>
          <button 
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-600" 
            onClick={() => toggleExpandChart('channels')}
            title="Expandir gráfico"
          >
            <Maximize2 size={18} />
          </button>
        </div>
        <div className="p-6">
          <MetricChart {...channelPerformanceConfig} />
        </div>
        <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Indicadores de desempenho por canal */}
          {channelsWithComparison.slice(0, 4).map((channel, idx) => (
            <div key={idx} className="bg-gray-50 rounded-lg p-3">
              <h3 className="font-medium text-sm flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2" 
                  style={{ backgroundColor: channelColors[channel.channel] || '#3B82F6' }}
                ></div>
                {channel.channel}
              </h3>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-gray-500">ROI</p>
                  <p className={`font-medium ${channel.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {channel.roi.toFixed(1)}%
                    {channel.roiChange !== 0 && (
                      <span className={`ml-1 ${channel.roiChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {channel.roiChange > 0 ? '↑' : '↓'} {Math.abs(channel.roiChange).toFixed(1)}%
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Receita</p>
                  <p className="font-medium">
                    R$ {formatNumber(channel.revenue)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gráfico de Top Produtos com expansão */}
      <div className="card shadow-md">
        <div className="card-header">
          <h2 className="card-title flex items-center">
            <BarChart2 className="h-5 w-5 text-blue-600 mr-2" />
            Top Produtos por Receita
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Total: R$ {formatNumber(totalRevenueProduct)}</span>
            <button 
              className="p-1 rounded-lg hover:bg-gray-100 text-gray-600"
              onClick={() => toggleExpandChart('products')}
              title="Expandir gráfico"
            >
              <Maximize2 size={18} />
            </button>
          </div>
        </div>
        <div className="p-6">
          <PerformanceBar {...productBarConfig} />
        </div>
        <div className="px-6 pb-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {topProducts.map((product, idx) => (
            <div key={idx} className="bg-gray-50 rounded-lg p-3">
              <h3 className="font-medium text-sm truncate" title={product.name}>{product.name}</h3>
              <div className="mt-2 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Receita:</span>
                  <span className="font-medium">R$ {formatNumber(product.revenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Lucro:</span>
                  <span className={`font-medium ${product.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    R$ {formatNumber(product.profit)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Margem:</span>
                  <span className="font-medium">{product.profitMargin.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gráfico de Análise ROI vs CPA com expansão */}
      <div className="card shadow-md">
        <div className="card-header">
          <h2 className="card-title flex items-center">
            <BarChart2 className="h-5 w-5 text-blue-600 mr-2" />
            Análise ROI vs. CPA por Canal
          </h2>
          <button 
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-600"
            onClick={() => toggleExpandChart('scatter')}
            title="Expandir gráfico"
          >
            <Maximize2 size={18} />
          </button>
        </div>
        <div className="p-6 h-80">
          <ScatterAnalysis {...scatterConfig} />
        </div>
        <div className="px-6 pb-6">
          <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
            <h3 className="font-medium flex items-center">
              <Info className="h-4 w-4 mr-1" />
              Como interpretar este gráfico
            </h3>
            <p className="text-xs mt-1">
              Este gráfico mostra a relação entre ROI e CPA por canal. Canais no quadrante superior esquerdo (alto ROI e baixo CPA) apresentam a melhor performance. O tamanho de cada bolha representa o volume de investimento.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
